<script setup>
/**
 * FormPublishBtn
 * A toggle switch for the 'status' field (Published/Unpublished).
 * Includes permission checks and safety logic to prevent unpublishing
 * critical items like the designated Home Page.
 */
import {computed, watch} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useUserStore} from '@/stores/user'
import {useFormStore} from '@/stores/form'
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

const formStore = useFormStore(props.formId)
const componentsStore = useComponentsStore()
const userStore = useUserStore()
const appStore = useAppStore()

// --- Computed ---

const currentData = computed(() => formStore.currentData || {})

const tableName = computed(() => formStore.tableName)

const id = computed(() => formStore.id)

const formSmall = computed(() => formStore.formSmall)

const statusLabel = computed(() => currentData.value.status ? 'Published' : 'Unpublished')

const component = computed(() => {
    return componentsStore.items.find(obj => obj.tableName === tableName.value)
})

const hasPublishAccess = computed(() => {
    if (tableName.value) {
        return userStore.access(tableName.value, 'publish')
    }
})

const show = computed(() => {
    return hasPublishAccess.value && currentData.value.status !== undefined
})

const allowPublish = computed(() => {
    // Safety check: Prevent unpublishing the site's home page
    if (tableName.value === 'pages') {
        return appStore.settings.homePage !== id.value
    }
    return true
})

// --- Watchers ---

watch(() => props.formId, () => {
    if (component.value?.showStatus && id.value === 0) {
        formStore.setDefaultValue({
            name: 'status',
            value: 0
        })
    }
}, {immediate: true})

// --- Methods ---

const onClick = () => {
    if (allowPublish.value) {
        currentData.value.status = !currentData.value.status ? 1 : 0
    }
}
</script>

<template>
    <button
        v-if="show"
        :class="[
            currentData.status ? 'btn-success' : 'btn-danger',
            {'btn-sm': formSmall}
        ]"
        :disabled="!allowPublish"
        class="btn wizard-form-control-publish"
        type="button"
        @click="onClick"
    >
        <span>{{ statusLabel }}</span>
    </button>
</template>